/**
 * 标签管理命令
 */

import { Command } from 'commander';
import { WorkflowManager } from '../core/workflow-manager';
import { TagManager } from '../core/tag-manager';
import { TaskStore } from '../core/task-store';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';
import path from 'path';
import inquirer from 'inquirer';

export function createTagsCommand(): Command {
  const command = new Command('tags');
  
  command
    .description('Manage task tags and groups')
    .alias('tag');
  
  // 列出所有标签
  command
    .command('list')
    .alias('ls')
    .description('List all tags with statistics')
    .option('-c, --category <category>', 'Filter by category')
    .option('-s, --sort <field>', 'Sort by field (name/count/recent)', 'count')
    .option('--json', 'Output as JSON')
    .action(withErrorHandling(async (options) => {
      const manager = new WorkflowManager();
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      const taskStore = new TaskStore();
      
      await tagManager.initialize();
      const tasks = await taskStore.getTasks();
      const stats = await tagManager.getStats(tasks);
      const allTags = tagManager.getAllTags();
      
      // 过滤
      let tags = allTags;
      if (options.category) {
        tags = tags.filter(tag => tag.category === options.category);
      }
      
      // 排序
      switch (options.sort) {
        case 'name':
          tags.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'recent':
          tags.sort((a, b) => b.lastUsed.getTime() - a.lastUsed.getTime());
          break;
        case 'count':
        default:
          tags.sort((a, b) => b.count - a.count);
          break;
      }
      
      if (options.json) {
        console.log(JSON.stringify({
          stats,
          tags: tags.map(tag => ({
            ...tag,
            lastUsed: tag.lastUsed.toISOString()
          }))
        }, null, 2));
        return;
      }
      
      // 显示统计信息
      console.log(colors.header('\n📊 Tag Statistics\n'));
      console.log(`Total tags: ${colors.highlight(stats.totalTags.toString())}`);
      console.log(`Tagged tasks: ${colors.highlight(stats.totalTaggedTasks.toString())}`);
      console.log();
      
      // 显示标签表格
      if (tags.length === 0) {
        console.log(colors.warning('No tags found'));
        return;
      }
      
      const table = new Table({
        columns: [
          { name: 'tag', title: 'Tag', alignment: 'left' },
          { name: 'count', title: 'Count', alignment: 'right' },
          { name: 'category', title: 'Category', alignment: 'left' },
          { name: 'lastUsed', title: 'Last Used', alignment: 'left' },
          { name: 'color', title: 'Color', alignment: 'center' }
        ]
      });
      
      for (const tag of tags) {
        table.addRow({
          tag: tag.color ? colors.hex(tag.color)(tag.name) : tag.name,
          count: tag.count,
          category: tag.category || '-',
          lastUsed: tag.lastUsed.toLocaleDateString(),
          color: tag.color ? '●' : '-'
        });
      }
      
      table.printTable();
      
      // 显示热门标签
      if (stats.topTags.length > 0) {
        console.log(colors.header('\n🔥 Top Tags:'));
        stats.topTags.slice(0, 5).forEach((item, index) => {
          console.log(`${index + 1}. ${colors.cyan(item.tag)} (${item.count} tasks)`);
        });
      }
    }));
  
  // 添加标签到任务
  command
    .command('add <taskId> <tags...>')
    .description('Add tags to a task')
    .action(withErrorHandling(async (taskId, tags) => {
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      const taskStore = new TaskStore();
      
      await tagManager.initialize();
      
      // 获取任务
      const tasks = await taskStore.getTasks();
      const task = tasks.find(t => t.id === taskId || t.content.includes(taskId));
      
      if (!task) {
        console.log(colors.error(`Task not found: ${taskId}`));
        return;
      }
      
      // 添加标签
      const updatedTask = await tagManager.addTags(task, tags);
      await taskStore.updateTask(task.id, updatedTask);
      
      console.log(colors.success(`✅ Added tags to task: ${tags.join(', ')}`));
      console.log(`Task: ${colors.dim(task.content.substring(0, 50))}...`);
      console.log(`Current tags: ${updatedTask.tags?.join(', ') || 'none'}`);
    }));
  
  // 从任务移除标签
  command
    .command('remove <taskId> <tags...>')
    .alias('rm')
    .description('Remove tags from a task')
    .action(withErrorHandling(async (taskId, tags) => {
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      const taskStore = new TaskStore();
      
      await tagManager.initialize();
      
      // 获取任务
      const tasks = await taskStore.getTasks();
      const task = tasks.find(t => t.id === taskId || t.content.includes(taskId));
      
      if (!task) {
        console.log(colors.error(`Task not found: ${taskId}`));
        return;
      }
      
      // 移除标签
      const updatedTask = await tagManager.removeTags(task, tags);
      await taskStore.updateTask(task.id, updatedTask);
      
      console.log(colors.success(`✅ Removed tags from task: ${tags.join(', ')}`));
      console.log(`Task: ${colors.dim(task.content.substring(0, 50))}...`);
      console.log(`Current tags: ${updatedTask.tags?.join(', ') || 'none'}`);
    }));
  
  // 搜索标签
  command
    .command('search <query>')
    .description('Search tags by name or description')
    .action(withErrorHandling(async (query) => {
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      
      await tagManager.initialize();
      const results = tagManager.searchTags(query);
      
      if (results.length === 0) {
        console.log(colors.warning(`No tags found matching: ${query}`));
        return;
      }
      
      console.log(colors.header(`\n🔍 Found ${results.length} tags:\n`));
      
      results.forEach(tag => {
        const color = tag.color ? colors.hex(tag.color) : colors.cyan;
        console.log(`${color(tag.name)} (${tag.count} uses)`);
        if (tag.description) {
          console.log(colors.dim(`  ${tag.description}`));
        }
      });
    }));
  
  // 建议标签
  command
    .command('suggest <taskId>')
    .description('Get tag suggestions for a task')
    .option('-n, --number <n>', 'Number of suggestions', '5')
    .action(withErrorHandling(async (taskId, options) => {
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      const taskStore = new TaskStore();
      
      await tagManager.initialize();
      
      // 获取任务
      const tasks = await taskStore.getTasks();
      const task = tasks.find(t => t.id === taskId || t.content.includes(taskId));
      
      if (!task) {
        console.log(colors.error(`Task not found: ${taskId}`));
        return;
      }
      
      console.log(colors.header('\n🤖 Tag Suggestions:\n'));
      console.log(`Task: ${colors.dim(task.content.substring(0, 80))}...`);
      console.log(`Current tags: ${task.tags?.join(', ') || 'none'}\n`);
      
      const suggestions = await tagManager.suggestTags(task, tasks);
      const topSuggestions = suggestions.slice(0, parseInt(options.number));
      
      if (topSuggestions.length === 0) {
        console.log(colors.warning('No suggestions available'));
        return;
      }
      
      topSuggestions.forEach((suggestion, index) => {
        const confidence = Math.round(suggestion.confidence * 100);
        const bar = '█'.repeat(Math.floor(confidence / 10)) + '░'.repeat(10 - Math.floor(confidence / 10));
        
        console.log(`${index + 1}. ${colors.cyan(suggestion.tag)}`);
        console.log(`   Confidence: ${bar} ${confidence}%`);
        console.log(`   Reason: ${colors.dim(suggestion.reason)}`);
        console.log();
      });
      
      // 询问是否应用建议
      const { apply } = await inquirer.prompt([{
        type: 'confirm',
        name: 'apply',
        message: 'Apply suggested tags?',
        default: false
      }]);
      
      if (apply) {
        const { selectedTags } = await inquirer.prompt([{
          type: 'checkbox',
          name: 'selectedTags',
          message: 'Select tags to apply:',
          choices: topSuggestions.map(s => ({
            name: `${s.tag} (${Math.round(s.confidence * 100)}%)`,
            value: s.tag
          }))
        }]);
        
        if (selectedTags.length > 0) {
          const updatedTask = await tagManager.addTags(task, selectedTags);
          await taskStore.updateTask(task.id, updatedTask);
          console.log(colors.success(`\n✅ Applied tags: ${selectedTags.join(', ')}`));
        }
      }
    }));
  
  // 重命名标签
  command
    .command('rename <oldName> <newName>')
    .description('Rename a tag across all tasks')
    .action(withErrorHandling(async (oldName, newName) => {
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      const taskStore = new TaskStore();
      
      await tagManager.initialize();
      const tasks = await taskStore.getTasks();
      
      const count = await tagManager.renameTag(oldName, newName, tasks);
      
      if (count > 0) {
        // 保存所有更新的任务
        for (const task of tasks) {
          await taskStore.updateTask(task.id, task);
        }
        
        console.log(colors.success(`✅ Renamed tag "${oldName}" to "${newName}" in ${count} tasks`));
      } else {
        console.log(colors.warning(`No tasks found with tag: ${oldName}`));
      }
    }));
  
  // 合并标签
  command
    .command('merge <sourceTags...>')
    .description('Merge multiple tags into one')
    .option('-t, --target <tag>', 'Target tag name (required)')
    .action(withErrorHandling(async (sourceTags, options) => {
      if (!options.target) {
        console.log(colors.error('Target tag is required. Use -t or --target'));
        return;
      }
      
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      const taskStore = new TaskStore();
      
      await tagManager.initialize();
      const tasks = await taskStore.getTasks();
      
      console.log(colors.info(`Merging tags: ${sourceTags.join(', ')} → ${options.target}`));
      
      const count = await tagManager.mergeTags(sourceTags, options.target, tasks);
      
      if (count > 0) {
        // 保存所有更新的任务
        for (const task of tasks) {
          await taskStore.updateTask(task.id, task);
        }
        
        console.log(colors.success(`✅ Merged tags in ${count} tasks`));
      } else {
        console.log(colors.warning('No tasks were updated'));
      }
    }));
  
  // 标签组管理
  const groupCommand = command
    .command('group')
    .alias('groups')
    .description('Manage tag groups');
  
  // 列出标签组
  groupCommand
    .command('list')
    .alias('ls')
    .description('List all tag groups')
    .action(withErrorHandling(async () => {
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      
      await tagManager.initialize();
      const groups = tagManager.getGroups();
      
      if (groups.length === 0) {
        console.log(colors.warning('No tag groups found'));
        console.log(colors.dim('Use "workflow tags group create" to create a group'));
        return;
      }
      
      console.log(colors.header('\n📁 Tag Groups:\n'));
      
      groups.forEach(group => {
        console.log(colors.cyan(group.name));
        if (group.description) {
          console.log(colors.dim(`  ${group.description}`));
        }
        console.log(`  Tags: ${group.tags.join(', ') || colors.dim('none')}`);
        console.log(`  Created: ${group.createdAt.toLocaleDateString()}`);
        console.log();
      });
    }));
  
  // 创建标签组
  groupCommand
    .command('create <name>')
    .description('Create a new tag group')
    .option('-d, --description <desc>', 'Group description')
    .option('-t, --tags <tags>', 'Initial tags (comma-separated)')
    .action(withErrorHandling(async (name, options) => {
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      
      await tagManager.initialize();
      
      const tags = options.tags ? options.tags.split(',').map((t: string) => t.trim()) : [];
      const group = await tagManager.createGroup(name, options.description, tags);
      
      console.log(colors.success(`✅ Created tag group: ${group.name}`));
      console.log(`ID: ${group.id}`);
      if (group.description) {
        console.log(`Description: ${group.description}`);
      }
      if (tags.length > 0) {
        console.log(`Tags: ${tags.join(', ')}`);
      }
    }));
  
  // 更新标签组
  groupCommand
    .command('update <groupId>')
    .description('Update a tag group')
    .option('-n, --name <name>', 'New group name')
    .option('-d, --description <desc>', 'New description')
    .option('-t, --tags <tags>', 'New tags (comma-separated, replaces all)')
    .option('--add-tags <tags>', 'Add tags to group (comma-separated)')
    .option('--remove-tags <tags>', 'Remove tags from group (comma-separated)')
    .action(withErrorHandling(async (groupId, options) => {
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      
      await tagManager.initialize();
      
      const groups = tagManager.getGroups();
      const group = groups.find(g => g.id === groupId || g.name === groupId);
      
      if (!group) {
        console.log(colors.error(`Group not found: ${groupId}`));
        return;
      }
      
      const updates: any = {};
      
      if (options.name) updates.name = options.name;
      if (options.description) updates.description = options.description;
      
      if (options.tags) {
        updates.tags = options.tags.split(',').map((t: string) => t.trim());
      } else if (options.addTags || options.removeTags) {
        const currentTags = new Set(group.tags);
        
        if (options.addTags) {
          options.addTags.split(',').forEach((t: string) => currentTags.add(t.trim()));
        }
        
        if (options.removeTags) {
          options.removeTags.split(',').forEach((t: string) => currentTags.delete(t.trim()));
        }
        
        updates.tags = Array.from(currentTags);
      }
      
      const updated = await tagManager.updateGroup(group.id, updates);
      
      if (updated) {
        console.log(colors.success(`✅ Updated tag group: ${updated.name}`));
      }
    }));
  
  // 删除标签组
  groupCommand
    .command('delete <groupId>')
    .alias('rm')
    .description('Delete a tag group')
    .action(withErrorHandling(async (groupId) => {
      const workflowDir = path.join(process.cwd(), '.workflow');
      const tagManager = new TagManager(workflowDir);
      
      await tagManager.initialize();
      
      const groups = tagManager.getGroups();
      const group = groups.find(g => g.id === groupId || g.name === groupId);
      
      if (!group) {
        console.log(colors.error(`Group not found: ${groupId}`));
        return;
      }
      
      const { confirm } = await inquirer.prompt([{
        type: 'confirm',
        name: 'confirm',
        message: `Delete group "${group.name}"?`,
        default: false
      }]);
      
      if (confirm) {
        const deleted = await tagManager.deleteGroup(group.id);
        if (deleted) {
          console.log(colors.success(`✅ Deleted tag group: ${group.name}`));
        }
      }
    }));
  
  return command;
}